import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

type Options = { title: string; description: string; confirmLabel?: string; cancelLabel?: string; danger?: boolean };
type Request = Options & { alert?: boolean; resolve: (result: boolean) => void };
type Dialogs = { confirm: (options: Options) => Promise<boolean>; alert: (options: Options) => Promise<boolean> };
const Context = createContext<Dialogs | null>(null);

export function DialogProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<Request | null>(null);
  const queue = useRef<Request[]>([]);
  const activeRef = useRef<Request | null>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const enqueue = useCallback((options: Options, alert: boolean) => new Promise<boolean>((resolve) => {
    const request = { ...options, alert, resolve };
    if (activeRef.current) queue.current.push(request);
    else { previousFocus.current = document.activeElement as HTMLElement | null; activeRef.current = request; setActive(request); }
  }), []);
  const finish = useCallback((result: boolean) => {
    const current = activeRef.current;
    if (!current) return;
    const next = queue.current.shift() ?? null;
    activeRef.current = next;
    setActive(next);
    current.resolve(result);
    if (!next) previousFocus.current?.focus();
  }, []);
  useEffect(() => {
    if (!active) return;
    dialogRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); finish(false); }
      if (event.key === "Tab") {
        const buttons = Array.from(dialogRef.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? []);
        if (!buttons.length) return;
        const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
        if (event.shiftKey && index <= 0) { event.preventDefault(); buttons[buttons.length - 1].focus(); }
        else if (!event.shiftKey && (index === buttons.length - 1 || index < 0)) { event.preventDefault(); buttons[0].focus(); }
      }
    };
    window.addEventListener("keydown", handleKey, true);
    return () => window.removeEventListener("keydown", handleKey, true);
  }, [active, finish]);
  return <Context.Provider value={{ confirm: (options) => enqueue(options, false), alert: (options) => enqueue(options, true) }}>
    {children}
    {active && createPortal(<div className="dialog-backdrop"><div ref={dialogRef} className="app-dialog" role="alertdialog" aria-modal="true" aria-labelledby="app-dialog-title" aria-describedby="app-dialog-description">
      <h2 id="app-dialog-title">{active.title}</h2><p id="app-dialog-description">{active.description}</p>
      <div className="app-dialog-actions">{!active.alert && <button type="button" className="page-button" onClick={() => finish(false)}>{active.cancelLabel || "Отмена"}</button>}
      <button type="button" className={`page-button ${active.danger ? "dialog-danger" : "primary"}`} onClick={() => finish(true)}>{active.confirmLabel || (active.alert ? "Понятно" : "Продолжить")}</button></div>
    </div></div>, document.body)}
  </Context.Provider>;
}
export function useDialog() { const context = useContext(Context); if (!context) throw new Error("DialogProvider is missing"); return context; }
